import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import logoImg from '../res/betterhusband_black.png';
import { blogIndexPath } from '../lib/blogPaths';
import '../styles/marketing.css';

/*
 * Shared shell for the public marketing pages rendered by React
 * (/blog, /landing/support, /landing/privacy, /landing/terms).
 * Visually matches the static homepage (frontend/public/landing/home/index.html).
 *
 * The homepage is a static file served by a Vercel rewrite, so links to it
 * MUST be plain <a href="/"> (full page load) — a react-router <Link to="/">
 * would hit the SPA's RootRedirect and land in onboarding instead.
 */
const STRINGS = {
  en: {
    play: 'Play', blog: 'Blog', support: 'Support', cta: 'Get the app',
    product: 'Product', how: 'How it works', pricing: 'Pricing', faq: 'FAQ',
    learn: 'Learn', company: 'Company', story: 'Our story',
    legal: 'Legal', privacy: 'Privacy Policy', terms: 'Terms of Service',
    tagline: 'Built by a husband, for husbands.',
  },
  it: {
    play: 'Gioca', blog: 'Blog', support: 'Supporto', cta: 'Scarica l’app',
    product: 'Prodotto', how: 'Come funziona', pricing: 'Prezzi', faq: 'FAQ',
    learn: 'Approfondisci', company: 'Azienda', story: 'La nostra storia',
    legal: 'Legale', privacy: 'Privacy Policy', terms: 'Termini di Servizio',
    tagline: 'Creata da un marito, per i mariti.',
  },
  es: {
    play: 'Jugar', blog: 'Blog', support: 'Soporte', cta: 'Descarga la app',
    product: 'Producto', how: 'Cómo funciona', pricing: 'Precios', faq: 'FAQ',
    learn: 'Aprende', company: 'Empresa', story: 'Nuestra historia',
    legal: 'Legal', privacy: 'Política de Privacidad', terms: 'Términos de Servicio',
    tagline: 'Creada por un marido, para maridos.',
  },
};

const MarketingLayout = ({ children }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('it') ? 'it' : i18n.language?.startsWith('es') ? 'es' : 'en';
  const t = STRINGS[lang];
  // The blog only exists in en and it; Spanish readers get the English edition.
  const blogHref = blogIndexPath(lang === 'it' ? 'it' : 'en');

  return (
    <div className="bh-marketing">
      <nav className="bh-nav" aria-label="Main">
        <div className="bh-nav-inner">
          <a className="bh-nav-brand" href="/">
            <img src={logoImg} alt="" width="32" height="32" />
            Better Husband.
          </a>
          <div className="bh-nav-links">
            {/* /play is prerendered and served as a static file, so a full page load. */}
            <a href="/play">{t.play}</a>
            <Link to={blogHref}>{t.blog}</Link>
            <Link to="/landing/support">{t.support}</Link>
            <LanguageSelector inline light />
            <a className="bh-nav-cta" href="/#download">{t.cta}</a>
          </div>
        </div>
      </nav>

      {children}

      <footer className="bh-footer">
        <div className="bh-footer-inner">
          <div className="bh-footer-cols">
            <div className="bh-footer-col">
              <h4>{t.product}</h4>
              <a href="/#how-it-works">{t.how}</a>
              <a href="/#pricing">{t.pricing}</a>
              <a href="/#faq">FAQ</a>
            </div>
            <div className="bh-footer-col">
              <h4>{t.learn}</h4>
              <Link to={blogHref}>{t.blog}</Link>
            </div>
            <div className="bh-footer-col">
              <h4>{t.company}</h4>
              <a href="/#story">{t.story}</a>
              <Link to="/landing/support">{t.support}</Link>
            </div>
            <div className="bh-footer-col">
              <h4>{t.legal}</h4>
              <Link to="/landing/privacy">{t.privacy}</Link>
              <Link to="/landing/terms">{t.terms}</Link>
            </div>
          </div>
          <div className="bh-footer-bottom">
            <span>© {new Date().getFullYear()} Better Husband — {t.tagline}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingLayout;
